/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */

import { useState } from "react";

import { useMutation } from "@tanstack/react-query";
import cn from "classnames";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import isValidEmail from "utils/isValidEmail";
import openToast from "utils/openToast";

import { verifyEmail } from "lib/v3/forgetPasswordApi";

import { GoBackIcon } from "components/icons";
import Stack from "components/ui/Stack";
import Text from "components/ui/Text";
import Label from "components/ui/Label";
import { Input } from "components/ui/Input";
import { Button } from "components/ui/Button";

import styles from "../style.module.css";

type ForgetPasswordProps = {
  setCurrentForm: (formName: string) => void;
  setEmail: (email: string) => void;
};

interface ForgetPasswordFormProps {
  email: string;
}

const ForgetPasswordForm: React.FC<ForgetPasswordProps> = ({
  setCurrentForm,
  setEmail,
}: any) => {
  const forgetPassword = useMutation(verifyEmail);
  const [isLoading, setIsLoading] = useState(false);

  const {
    watch,
    formState: { errors },
    register,
    handleSubmit,
  } = useForm<ForgetPasswordFormProps>({
    defaultValues: {
      email: "",
    },
  });

  const navigate = useNavigate();

  const onSubmit: SubmitHandler<ForgetPasswordFormProps> = (data) => {
    setIsLoading(true);
    const email = data.email;
    forgetPassword.mutate(
      { email },
      {
        onSuccess: ({ data }) => {
          setIsLoading(false);

          openToast(
            data.message ||
              "Verification code has been sent to your email address.",
            "success",
          );

          setEmail(email);

          setCurrentForm(data.step);
        },
        onError: (error: any) => {
          setIsLoading(false);
          openToast(error.response.data.error, "error");
        },
      },
    );
  };

  const email = watch("email");

  const isDisabled = !email || !isValidEmail(email);

  return (
    <form
      className="flex flex-col w-full max-w-[545px] items-center"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Stack className="space-y-10">
        <Stack className="space-y-8">
          <Stack className="gap-1">
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => navigate("/login")}>
                <GoBackIcon fill="#fff" width="20" height="20" />
              </button>

              <h3 className="text-white text-lg md:text-xl font-bold">
                Forgot Your Password?
              </h3>
            </div>

            <Text className="text-sm text-[#9A9A9A]">
              Enter your email address below. We will send you a verification
              code to reset your password.
            </Text>
          </Stack>

          <Stack className="mb-5">
            <Label
              htmlFor="email"
              isBold={false}
              className="font-bold text-base text-c-gray-200 mb-2"
            >
              Email
            </Label>

            <Input
              id="email"
              placeholder="john.doe@org.com"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,7}$/,
                  message: "Please enter a valid email address",
                },
              })}
            />

            {errors.email && (
              <Text className="mt-2 text-alert">{errors.email.message}</Text>
            )}
          </Stack>
        </Stack>

        <Button
          variant="primary"
          type="submit"
          size="xl"
          shouldPreventDefault={false}
          isLoading={isLoading}
          className={cn({
            [styles.disabled]: isDisabled,
          })}
          disabled={isDisabled}
        >
          Reset Password
        </Button>
      </Stack>
    </form>
  );
};

export default ForgetPasswordForm;
